import { Navigate, useLocation } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { UNGUARDED_ROUTES } from '~/constants/config/unguarded-routes';
import { useScreenAccess } from '~/hooks/use-screen-access';
import { useTenantAdmin } from '~/hooks/useTenantAdmin';
import { useAuthStore } from '~/store/auth.store';

interface ScreenGuardProps {
  children: ReactNode;
  fallback?: string;
}

/**
 * API-driven route access guard.
 *
 * Checks if the current pathname is in the allowed paths returned by
 * GET /screens/navigation. The backend handles all permission filtering —
 * the frontend trusts the API response.
 *
 * Access layers:
 * 1. No authenticated user — bail; `requireAuth` on `_authenticated` owns the redirect
 * 2. Super admins and tenant admins bypass all checks
 * 3. Unguarded routes bypass ScreenGuard
 * 4. Wait for navigation data to load
 * 5. Check current path against API-allowed paths
 */
export function ScreenGuard({ children, fallback = '/access-denied' }: ScreenGuardProps) {
  const { pathname } = useLocation();
  const { isPathAllowed, isLoading } = useScreenAccess();
  const isTenantAdmin = useTenantAdmin();
  const user = useAuthStore((s) => s.user);

  // Layer 1: No user (mid-logout or pre-auth) — don't race the auth guard by
  // bouncing to /access-denied. `requireAuth` on `_authenticated` will redirect
  // to /login on the next routing pass. Returning children keeps the current
  // render harmless until that lands.
  if (!user) return <>{children}</>;

  // Layer 2: Super admins and tenant admins bypass all checks
  if (user.isSuperAdmin || isTenantAdmin) return <>{children}</>;

  // Layer 3: Unguarded routes bypass ScreenGuard
  if (isUnguardedRoute(pathname)) return <>{children}</>;

  // Layer 4: Wait for navigation data to load
  if (isLoading) return <PageSkeleton />;

  // Layer 5: Check if current path is in API-allowed paths
  if (!isPathAllowed(pathname)) return <Navigate to={fallback} />;

  return <>{children}</>;
}

function isUnguardedRoute(pathname: string): boolean {
  return UNGUARDED_ROUTES.some((route) => pathname === route || pathname.startsWith(route + '/'));
}
